import type {
  MotorPerformanceChartContext,
  MotorPerformanceChartData
} from "./d";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type {
  Equipment,
  OperatingParameters
} from "@/composables/forms/formTypes";
import { getMotorPerformancePoints } from "./getMotorPerformancePoints";
import type { Motors } from "gql/api";
import { getMotorOperatingPoints } from "./getMotorOperatingPoints";
import { getExtents, getRange } from "@incytestudios/ddv";
import type { OperatingPoint } from "@/components/types";
import { Ratio } from "@/libs/units/percentage";
import {
  getMotorCurrentA,
  getMotorEfficiencyRatio,
  getMotorPowerFactor,
  getMotorSpeedRPM
} from "@/libs/calculations/motors";

const buildMotorPerformanceChartContext = (
  equipment: Equipment,
  parameters: OperatingParameters,
  calcs: ScenarioCalculations
): MotorPerformanceChartContext | undefined => {
  const motor = equipment?.r_motor;
  if (!motor) return undefined;

  const motorLoadRatio = calcs.motorCalculations.motorLoadRatio.value;
  const pumpHz = calcs.pumpHz.value;
  const motorRerateRatio = parameters?.motor_rerate_ratio;
  const motorVoltageCorrections =
    calcs.motorCalculations.motorVoltageCorrections.value;

  // Need to calculate these with RR since normally they are calc'd with NP
  // from the actual scenario calculations
  const motorEfficiencyRatio = getMotorEfficiencyRatio(
    motorLoadRatio,
    motor.effb,
    motor.eff1,
    motor.eff2,
    motor.eff3,
    motor.eff4,
    motor.eff5,
    motorVoltageCorrections.efficiencyCorrection
  );
  const motorPowerFactor = getMotorPowerFactor(
    motorLoadRatio,
    motor.pfb,
    motor.pf1,
    motor.pf2,
    motor.pf3,
    motor.pf4,
    motor.pf5,
    motorVoltageCorrections.powerFactorCorrection
  );
  const motorCurrentA = getMotorCurrentA(
    motorLoadRatio,
    1,
    motor.ib,
    motor.i1,
    motor.i2,
    motor.i3,
    motor.i4,
    motor.i5,
    motorVoltageCorrections.ampCorrection
  );
  const motorOperatingRPM = getMotorSpeedRPM(
    motorLoadRatio,
    pumpHz,
    motor.nb,
    motor.n1,
    motor.n2,
    motor.n3,
    motor.n4,
    motor.n5,
    motorVoltageCorrections.rpmCorrection
  );
  const context: MotorPerformanceChartContext = {
    motor: motor as Motors,
    pumpHz,
    motorRerateRatio,
    motorVoltageCorrections,
    minLoad: Math.min(0.4, calcs.motorCalculations.motorLoadRatio.value),
    maxLoad: Math.max(1.2, calcs.motorCalculations.motorLoadRatio.value),
    iterations: 12,
    motorLoadRatio,
    motorLoadRatioNP: calcs.motorCalculations.motorLoadRatioNP.value,
    motorEfficiencyRatio,
    motorPowerFactor,
    motorCurrentA,
    motorOperatingRPM
  };

  return context;
};

export const useMotorPerformanceChartData = (
  equipment: Equipment,
  parameters: OperatingParameters,
  scenarioCalculations: ScenarioCalculations
): MotorPerformanceChartData | undefined => {
  const context = buildMotorPerformanceChartContext(
    equipment,
    parameters,
    scenarioCalculations
  );

  if (!context) {
    return undefined;
  } else {
    const { motor } = context;
    // All line datasets on chart
    const motorPerformancePoints = getMotorPerformancePoints(context);
    const motorEfficiencyDataSet = {
      title: "Efficiency",
      points: motorPerformancePoints.map(data => data.efficiency),
      xAxisLabel: "% Motor Load",
      yAxisLabel: "%"
    };
    const motorPowerFactorDataSet = {
      title: "Power Factor",
      points: motorPerformancePoints.map(data => data.powerFactor),
      yAxisLabel: "%"
    };
    const motorCurrentDataSet = {
      title: "Percent Current",
      points: motorPerformancePoints.map(data => data.current),
      yAxisLabel: "%"
    };
    const motorNameplateCurrentDataSet = {
      title: "Speed RPM",
      points: motorPerformancePoints.map(data => data.nameplateCurrent),
      yAxisLabel: "RPM"
    };
    // Vertical line on chart
    const operatingPointThreshold = {
      value: Ratio.toStandardUnit(
        scenarioCalculations.motorCalculations.motorLoadRatio.value || 1
      ),
      color: "var(--plotColor4)"
    };
    // Operating points on vertical line (motor load ratio line)
    const percentageBasedOperatingPoints = getMotorOperatingPoints(context);
    const speedRpmOperatingPoint: OperatingPoint = {
      label: "Speed RPM",
      color: "var(--plotColor3)",
      point: {
        x: Ratio.toStandardUnit(
          scenarioCalculations.motorCalculations?.motorLoadRatio.value || 1
        ),
        y: context.motorOperatingRPM
      },
      yLabel: "RPM"
    };
    const speedRpmDomain = getExtents([
      ...motorNameplateCurrentDataSet.points.map(({ y }) =>
        typeof y === "number" ? y : 0
      ),
      typeof speedRpmOperatingPoint.point.y === "number"
        ? speedRpmOperatingPoint.point.y
        : 0
    ]);
    const paddedSpeedRpmDomain = {
      ...speedRpmDomain,
      max: speedRpmDomain.max + getRange(speedRpmDomain) * 0.1
    };

    const adjustedReratedNameplateHP =
      scenarioCalculations.motorCalculations.adjustedReratedNameplateHP.value;
    const adjustedReratedNameplateV =
      scenarioCalculations.motorCalculations.adjustedReratedNameplateV.value;
    const adjustedReratedNameplateA =
      scenarioCalculations.motorCalculations.adjustedReratedNameplateA.value;

    const retval: MotorPerformanceChartData = {
      xDomain: {
        min: Ratio.toStandardUnit(context.minLoad),
        max: Ratio.toStandardUnit(context.maxLoad)
      },
      motor,
      motorPowerFactorDataSet,
      motorEfficiencyDataSet,
      motorCurrentDataSet,
      motorNameplateCurrentDataSet,
      percentageBasedOperatingPoints,
      speedRpmOperatingPoint,
      speedRpmDomain,
      paddedSpeedRpmDomain,
      operatingPointThreshold,
      adjustedReratedNameplateA,
      adjustedReratedNameplateV,
      adjustedReratedNameplateHP
    };

    return retval;
  }
};
